import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface ForgotPasswordFormData {
  email: string;
}

// Helper function to get CSRF token from window.initialData
const getCSRFToken = (): string => {
  // Check if window.initialData.csrfToken exists
  if (window.initialData && window.initialData.csrfToken) {
    return window.initialData.csrfToken;
  }
  // Fallback to empty string
  return '';
};

export const ForgotPassword: React.FC = () => {
  const [formData, setFormData] = useState<ForgotPasswordFormData>({
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/auth/password/reset/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
        },
        body: JSON.stringify(formData),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.errors || 'An error occurred. Please try again.');
        return;
      }

      setSuccess(true);
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', {
          state: {
            message: 'Password reset email sent. Please check your inbox.'
          }
        });
      }, 3000);
    } catch (error) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-content">
      <div className="auth-header">
        <h1 className="hero-title">Reset Password</h1>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label">Email</label>
          <input
            type="email"
            className="input"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>
        {error && (
          <div className="form-message is-error">
            {error}
          </div>
        )}
        {success && (
          <div className="form-message is-success">
            {success}
          </div>
        )}
        <div className="form-actions">
          <button
            type="submit"
            className="button is-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <div className="loading-spinner" />
            ) : (
              'Reset Password'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

// Add TypeScript declaration for window.initialData
declare global {
  interface Window {
    initialData?: {
      user?: {
        email: string;
        initials: string;
      };
      csrfToken: string;
    };
  }
}
